if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // Subscribe to variant changes for alt-text-based image filtering.
        // Placed BEFORE the thumbnails guard so it runs on ALL gallery layouts.
        this.variantFilterUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const sectionId = this.id.replace('MediaGallery-', '').replace('-duplicate', '');
          if (event.data.sectionId !== sectionId) return;
          const variant = event.data.variant;
          if (!variant) return;

          this.filterByVariant(variant);
        });

        // Thumbnail-dependent listeners — only set up if thumbnails exist
        if (!this.elements.thumbnails) return;
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        // Apply filtering on page load to match the initially selected variant
        requestAnimationFrame(() => this.applyInitialFilter());
      }

      applyInitialFilter() {
        const sectionId = this.id.replace('MediaGallery-', '').replace('-duplicate', '');

        // Find the variant ID input scoped to this section's product form
        const variantIdInput = document.querySelector(
          `#product-form-${sectionId} .product-variant-id, [data-section="${sectionId}"] .product-variant-id`
        );
        if (!variantIdInput || !variantIdInput.value) return;

        // Find the variant JSON scoped to this section
        const variantDataEl = document.querySelector(
          `variant-selects[data-section="${sectionId}"] [type="application/json"],
           variant-radios[data-section="${sectionId}"] [type="application/json"]`
        );
        if (!variantDataEl) return;

        try {
          const variants = JSON.parse(variantDataEl.textContent);
          const currentVariant = variants.find((v) => String(v.id) === String(variantIdInput.value));
          if (currentVariant) this.filterByVariant(currentVariant);
        } catch (e) {
          console.error('[District Loom] variant filter init error:', e);
        }
      }

      filterByVariant(variant) {
        // Lowercase option values for this variant
        const optionValues = new Set(
          [variant.option1, variant.option2, variant.option3]
            .filter(Boolean)
            .map((v) => v.toLowerCase().trim())
        );

        const allViewerItems = this.elements.viewer.querySelectorAll('[data-media-id]');

        // Only activate if at least one image uses the :: tagging convention.
        // Products with no :: tagged images are completely unaffected.
        const galleryUsesAltFiltering = [...allViewerItems].some((item) =>
          (item.dataset.alt || '').includes('::')
        );
        if (!galleryUsesAltFiltering) return;

        // Extract the tag before :: (lowercased), or null if no :: present
        const getTag = (rawAlt) => {
          if (!(rawAlt || '').includes('::')) return null;
          return rawAlt.split('::')[0].toLowerCase().trim();
        };

        const shouldShow = (item) => {
          const rawAlt = (item.dataset.alt || '').trim();
          if (rawAlt === '') return true; // empty alt = truly global
          const tag = getTag(rawAlt);
          if (tag === null) return false; // has alt text but no :: = belongs to another variant
          return optionValues.has(tag) || [...optionValues].some((v) => tag.includes(v) || v.includes(tag));
        };

        // Split into visible and hidden
        const visibleItems = [...allViewerItems].filter((item) => shouldShow(item));
        const hiddenItems  = [...allViewerItems].filter((item) => !shouldShow(item));

        // Prepend the first matching item to the DOM front so the CSS :first-child
        // rule gives it full width. Runs on both page load AND variant switch.
        const sectionId = this.id.replace('MediaGallery-', '').replace('-duplicate', '');
        let toPrepend = null;
        if (variant.featured_media) {
          toPrepend = this.elements.viewer.querySelector(
            `[data-media-id="${sectionId}-${variant.featured_media.id}"]`
          );
        }
        if (!toPrepend) {
          toPrepend = [...allViewerItems].find(el => {
            const tag = getTag(el.dataset.alt || '');
            return tag && (optionValues.has(tag) || [...optionValues].some(v => tag.includes(v) || v.includes(tag)));
          });
        }
        if (toPrepend) toPrepend.parentElement?.prepend(toPrepend);

        // Show/hide
        visibleItems.forEach((item) => { item.style.display = ''; });
        hiddenItems.forEach((item)  => { item.style.display = 'none'; });

        // Mark first visible item as active
        const firstVisibleItem = visibleItems[0] || null;
        allViewerItems.forEach((el) => el.classList.remove('is-active'));
        if (firstVisibleItem) firstVisibleItem.classList.add('is-active');

        // Mirror onto thumbnail strip — same reorder logic
        if (this.elements.thumbnails) {
          const allThumbs = [...this.elements.thumbnails.querySelectorAll('[data-target]')];
          const visibleThumbs = allThumbs.filter((t) => shouldShow(t));
          const hiddenThumbs  = allThumbs.filter((t) => !shouldShow(t));
          visibleThumbs.forEach((t) => { t.style.display = ''; });
          hiddenThumbs.forEach((t)  => { t.style.display = 'none'; });
          const thumbList = this.elements.thumbnails.querySelector('.thumbnail-list');
          if (thumbList) {
            visibleThumbs.forEach((t) => thumbList.appendChild(t));
            hiddenThumbs.forEach((t)  => thumbList.appendChild(t));
          }
        }
      }

      disconnectedCallback() {
        if (this.variantFilterUnsubscriber) this.variantFilterUnsubscriber();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}